// src/pages/Operations.jsx
// Unified Operations page combining Jobs, Workflows, and Scheduled Jobs
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGraphQL } from '../graphql';
import { useWorkflowNavigation } from '../context/WorkflowNavigationContext';
import TabNavigation from '../components/operations/TabNavigation';
import Breadcrumb from '../components/Breadcrumb';
import JobsTab from '../components/operations/JobsTab';
import WorkflowsTab from '../components/operations/WorkflowsTab';
import ScheduledTab from '../components/operations/ScheduledTab';
import CreateJobModal from '../components/CreateJobModal';
import JobManagementModal from '../components/JobManagementModal';
import {
    PlusIcon,
    ArrowPathIcon,
    WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

const Operations = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { executeQuery, queries, loading } = useGraphQL();
    const { workflowContext, clearWorkflowContext } = useWorkflowNavigation();

    // Tab state from URL or default
    const [activeTab, setActiveTab] = useState(() => {
        return searchParams.get('tab') || 'jobs';
    });

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showManagementModal, setShowManagementModal] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);

    // Stats for tab badges
    const [stats, setStats] = useState({
        jobs: 0,
        workflows: 0,
        scheduled: 0,
    });

    // Selected items (for navigation from workflows)
    const [selectedJobId, setSelectedJobId] = useState(() => {
        return searchParams.get('jobId') || null;
    });
    const [selectedRunId, setSelectedRunId] = useState(() => {
        return searchParams.get('runId') || null;
    });

    // Refresh callback for child tabs
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const triggerRefresh = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    // Update URL when tab changes
    useEffect(() => {
        const params = new URLSearchParams();
        params.set('tab', activeTab);
        if (selectedJobId && activeTab === 'jobs') {
            params.set('jobId', selectedJobId);
        }
        if (selectedRunId && activeTab === 'workflows') {
            params.set('runId', selectedRunId);
        }
        setSearchParams(params, { replace: true });
    }, [activeTab, selectedJobId, selectedRunId, setSearchParams]);

    // Load stats periodically - use small targeted queries
    useEffect(() => {
        const loadStats = async () => {
            try {
                // Make parallel requests for running jobs and scheduled jobs
                const [runningData, scheduledData] = await Promise.all([
                    executeQuery(queries.getJobs(), { limit: 100, offset: 0, status: 'RUNNING', isScheduled: false }),
                    executeQuery(queries.getJobs(), { limit: 200, offset: 0, isScheduled: true }),
                ]);

                const runningJobs = (runningData?.adminJobs || []).length;
                const scheduledCount = (scheduledData?.adminJobs || []).filter(j => j.enabled).length;

                setStats({
                    jobs: runningJobs,
                    workflows: 0, // Will be updated by WorkflowsTab
                    scheduled: scheduledCount,
                });
            } catch (err) {
                console.error('Failed to load stats:', err);
            }
        };

        loadStats();
        if (autoRefresh) {
            const interval = setInterval(loadStats, 10000);
            return () => clearInterval(interval);
        }
    }, [autoRefresh, refreshTrigger]);

    // Handle navigation from workflow to job
    const handleNavigateToJob = useCallback((jobId) => {
        setSelectedJobId(jobId);
        setActiveTab('jobs');
    }, []);

    // Handle back to workflow navigation
    const handleBackToWorkflow = useCallback(() => {
        if (workflowContext) {
            setSelectedRunId(workflowContext.runId);
            setActiveTab('workflows');
            clearWorkflowContext();
        }
    }, [workflowContext, clearWorkflowContext]);

    // Clear selected job when switching tabs
    const handleTabChange = useCallback((tab) => {
        if (tab !== 'jobs') {
            setSelectedJobId(null);
        }
        if (tab !== 'workflows') {
            setSelectedRunId(null);
        }
        setActiveTab(tab);
    }, []);

    // Build breadcrumb items when viewing job from workflow
    const breadcrumbItems = workflowContext ? [
        { label: 'WORKFLOWS', onClick: handleBackToWorkflow },
        { label: workflowContext.templateName?.replace(/-/g, ' ').toUpperCase() || 'WORKFLOW' },
        { label: workflowContext.stepId || 'STEP' },
    ] : null;

    return (
        <div className="h-full flex flex-col bg-gray-950">
            {/* Modals */}
            <CreateJobModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onJobCreated={triggerRefresh}
            />
            <JobManagementModal
                isOpen={showManagementModal}
                onClose={() => setShowManagementModal(false)}
                onAction={triggerRefresh}
            />

            {/* Header */}
            <div className="border-b border-slate-200 bg-white">
                <div className="flex items-center justify-between px-4 py-3">
                    <h1 className="text-lg font-mono font-bold text-slate-900 tracking-tight">
                        OPERATIONS
                    </h1>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowManagementModal(true)}
                            className="px-3 py-1.5 rounded-lg bg-white text-slate-700 text-sm font-mono border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150"
                        >
                            <WrenchScrewdriverIcon className="h-4 w-4 inline mr-1" />
                            MANAGE
                        </button>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="px-3 py-1.5 rounded-lg bg-primary text-on-accent text-sm font-mono font-bold hover:brightness-110 transition-all duration-150"
                        >
                            <PlusIcon className="h-4 w-4 inline mr-1" />
                            NEW JOB
                        </button>
                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-mono border transition-all duration-150 ${
                                autoRefresh
                                    ? 'bg-primary text-on-accent border-primary'
                                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                            }`}
                            title={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
                        >
                            <ArrowPathIcon className={`h-4 w-4 inline ${autoRefresh ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={triggerRefresh}
                            disabled={loading}
                            className="px-3 py-1.5 rounded-lg bg-white text-slate-700 text-sm font-mono border border-slate-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 transition-all duration-150"
                        >
                            REFRESH
                        </button>
                    </div>
                </div>

                {/* Tab Navigation */}
                <TabNavigation
                    activeTab={activeTab}
                    onChange={handleTabChange}
                    stats={stats}
                />
            </div>

            {/* Workflow Context Breadcrumb */}
            {breadcrumbItems && activeTab === 'jobs' && (
                <Breadcrumb
                    items={breadcrumbItems}
                    onBack={handleBackToWorkflow}
                    backLabel="BACK TO WORKFLOW"
                />
            )}

            {/* Tab Content */}
            <div className="flex-1 min-h-0 overflow-hidden">
                {activeTab === 'jobs' && (
                    <JobsTab
                        selectedJobId={selectedJobId}
                        onSelectJob={setSelectedJobId}
                        autoRefresh={autoRefresh}
                        refreshTrigger={refreshTrigger}
                        onRefresh={triggerRefresh}
                    />
                )}
                {activeTab === 'workflows' && (
                    <WorkflowsTab
                        selectedRunId={selectedRunId}
                        onSelectRun={setSelectedRunId}
                        onNavigateToJob={handleNavigateToJob}
                        autoRefresh={autoRefresh}
                        refreshTrigger={refreshTrigger}
                        onStatsUpdate={(runningCount) => setStats(prev => ({ ...prev, workflows: runningCount }))}
                    />
                )}
                {activeTab === 'scheduled' && (
                    <ScheduledTab
                        autoRefresh={autoRefresh}
                        refreshTrigger={refreshTrigger}
                        onRefresh={triggerRefresh}
                    />
                )}
            </div>
        </div>
    );
};

export default Operations;
